import React from 'react';
import PropTypes from 'prop-types';
import XLSX from 'xlsx-color';

import { getEEBoxStyle } from '../../../utils/functions';
import colors from '../../../sass/colors';

export const getExcelFileName = (prefix, defaultSettings = {}) => {
    const { report_name_or_client_name: reportName = '', report_id: reportId = '' } = defaultSettings;
    const currentDate = new Date().toLocaleDateString();
    const currentTimestamp = new Date().toLocaleTimeString();
    return `${prefix}_${reportName}_${reportId}_${currentDate}_${currentTimestamp}.xlsx`;
};

export const encodeFillCell = (bg, color, value) => {
    const bgHex = `${bg || '#ffffff'}`.replace('#', '');
    const colorHex = `${color || '#000000'}`.replace('#', '');
    return `fill#${bgHex}#${colorHex}#${value}`;
};

export const formatScoreValue = score => {
    if (score === null || score === undefined || score === '') return '-';
    if (!isNaN(parseInt(score, 10))) {
        return typeof score === 'number' ? score.toLocaleString('en-US') : score;
    }
    return '-';
};

export const isNSizeTitle = title => {
    const text = `${title || ''}`.trim();
    return text.toLowerCase() === 'n';
};

export const getIeScoreCell = ({
    score,
    quartile = '',
    isNegativeTrait = false,
    isN = false,
    applyBenchmark = false,
    quartileColors = [],
    isNonProfit = false,
    ohid = '',
    forceQuartile = false,
}) => {
    const value = formatScoreValue(score);
    if (isN) {
        return encodeFillCell('#ffffff', colors.$darkBlue200, value);
    }
    if (value === '-') {
        return encodeFillCell('#ffffff', colors.$darkBlue200, value);
    }
    if (applyBenchmark || forceQuartile) {
        const { background = '#ffffff', color = colors.$darkBlue100 } = quartileColors[quartile] || {};
        const isTopDecile = parseInt(quartile, 10) === 0;
        const fillBg = isTopDecile ? '#FFA800' : background;
        return encodeFillCell(fillBg, color, value);
    }
    const { positiveBg = '#ffffff', negativeBg = '#ffffff', color: textColor = '#000000' } =
        getEEBoxStyle(score, isNegativeTrait, isNonProfit, ohid) || {};
    const bg = isNegativeTrait ? negativeBg : positiveBg;
    return encodeFillCell(bg, textColor, value);
};

export const DIFF_EXCEL_COLORS = {
    neg: { bg: '#CD3030', color: '#FFFFFF' },
    pos: { bg: '#10CBBC', color: '#FFFFFF' },
    neutral: { bg: '#E6D7BC', color: '#000000' },
    'neutral-meaningful': { bg: '#FECF8D', color: '#000000' },
    largeDec: { bg: '#E33B3B', color: '#FFFFFF' },
    smallDec: { bg: '#F17E7E', color: '#FFFFFF' },
    smallInc: { bg: '#64BDBC', color: '#000000' },
    largeInc: { bg: '#108980', color: '#FFFFFF' },
};

export const writeColoredExcel = (tableId, fileName, options = {}) => {
    const table = document.getElementById(tableId);
    if (!table) return null;
    const preventDateCoercion = options.preventDateCoercion === true;
    const wb = XLSX.utils.table_to_book(table, {
        sheet: 'Report',
        ...(preventDateCoercion ? { raw: true } : {}),
    });
    Object.keys(wb.Sheets.Report).forEach(val => {
        const cell = wb.Sheets.Report[val];
        if (!cell || typeof cell !== 'object' || cell.v === undefined) return;
        const valueToSplit = cell.v;
        const split = String(valueToSplit).split('#') || [];
        if (!split.length) return;

        const splitFirst = split[0];
        let displayValue = split[1] || '';
        let background = 'FFFFFF';
        let color = '000000';
        const bold = splitFirst === 'bold';
        let align = bold ? 'left' : 'center';

        if (splitFirst === 'fill') {
            background = split[1] || 'FFFFFF';
            color = split[2] || '000000';
            displayValue = split.slice(3).join('#');
            align = 'center';
        } else if (splitFirst === 'bold') {
            displayValue = split[1] || '';
            if (split.length === 3) {
                align = 'center';
            }
        } else if (splitFirst === '') {
            displayValue = split.slice(1).join('#');
            align = 'left';
        } else {
            displayValue = valueToSplit;
            align = 'left';
        }

        const borderObj = {
            style: 'thin',
            color: { rgb: 'FFFFFF' },
        };
        cell.v = preventDateCoercion ? String(displayValue == null ? '' : displayValue) : displayValue;
        if (preventDateCoercion) {
            cell.t = 's';
            cell.z = '@';
            delete cell.w;
        }
        cell.s = {
            ...(preventDateCoercion ? { numFmt: '@' } : {}),
            font: {
                color: { rgb: `${color}`.replace('#', '') },
                bold,
                sz: bold ? 14 : 12,
            },
            alignment: {
                vertical: align,
                horizontal: align,
            },
            fill: {
                patternType: 'solid',
                fgColor: { rgb: `${background}`.replace('#', '') },
            },
            border: {
                bottom: borderObj,
                top: borderObj,
                left: borderObj,
                right: borderObj,
            },
        };
    });
    return XLSX.writeFile(wb, fileName);
};

export function ExcelFilterRows({
    selectedLabel = '',
    demoFilterLabels = [],
    benchLabel = '',
    exBenchLabel = '',
    years = null,
    demoFilterLabelsByYear = [],
    benchLabelsByYear = [],
    exBenchLabelsByYear = [],
    staticText = {},
}) {
    const { DEMO_SELECTED_OPTIONS, BENCHMARK_FILTERS } = staticText;
    if (years) {
        return (
            <>
                <tbody>
                    <tr>
                        <th>bold#{DEMO_SELECTED_OPTIONS}</th>
                        {selectedLabel ? <th>#{selectedLabel}</th> : null}
                    </tr>
                </tbody>
                <tbody>
                    <tr>
                        <th>bold#Filters</th>
                    </tr>
                    <tr>
                        <td>bold#Demographic-filters({years[0]})</td>
                        {(demoFilterLabelsByYear[0] || []).map(label => (
                            <td key={`d0-${label}`}>#{label}</td>
                        ))}
                    </tr>
                    <tr>
                        <td>bold#Demographic-filters({years[1]})</td>
                        {(demoFilterLabelsByYear[1] || []).map(label => (
                            <td key={`d1-${label}`}>#{label}</td>
                        ))}
                    </tr>
                    <tr>
                        <td>
                            bold#{BENCHMARK_FILTERS}({years[0]})
                        </td>
                        {benchLabelsByYear[0] ? <td>#{benchLabelsByYear[0]}</td> : null}
                    </tr>
                    <tr>
                        <td>
                            bold#{BENCHMARK_FILTERS}({years[1]})
                        </td>
                        {benchLabelsByYear[1] ? <td>#{benchLabelsByYear[1]}</td> : null}
                    </tr>
                    {exBenchLabelsByYear.length ? (
                        <>
                            <tr>
                                <td>bold#EX Benchmark({years[0]})</td>
                                {exBenchLabelsByYear[0] ? <td>#{exBenchLabelsByYear[0]}</td> : null}
                            </tr>
                            <tr>
                                <td>bold#EX Benchmark({years[1]})</td>
                                {exBenchLabelsByYear[1] ? <td>#{exBenchLabelsByYear[1]}</td> : null}
                            </tr>
                        </>
                    ) : null}
                </tbody>
            </>
        );
    }

    return (
        <>
            <tbody>
                <tr>
                    <th>bold#{DEMO_SELECTED_OPTIONS}</th>
                    {selectedLabel ? <th>#{selectedLabel}</th> : null}
                </tr>
            </tbody>
            <tbody>
                <tr>
                    <th>bold#Filters</th>
                </tr>
                <tr>
                    <td>bold#Demographic-filters</td>
                    {demoFilterLabels.map(label => (
                        <td key={label}>#{label}</td>
                    ))}
                </tr>
                <tr>
                    <td>bold#{BENCHMARK_FILTERS}</td>
                    {benchLabel ? <td>#{benchLabel}</td> : null}
                </tr>
                <tr>
                    <td>bold#EX Benchmark</td>
                    {exBenchLabel ? <td>#{exBenchLabel}</td> : null}
                </tr>
            </tbody>
        </>
    );
}

ExcelFilterRows.propTypes = {
    selectedLabel: PropTypes.string,
    demoFilterLabels: PropTypes.array,
    benchLabel: PropTypes.string,
    exBenchLabel: PropTypes.string,
    years: PropTypes.array,
    demoFilterLabelsByYear: PropTypes.array,
    benchLabelsByYear: PropTypes.array,
    exBenchLabelsByYear: PropTypes.array,
    staticText: PropTypes.object,
};

ExcelFilterRows.defaultProps = {
    selectedLabel: '',
    demoFilterLabels: [],
    benchLabel: '',
    exBenchLabel: '',
    years: null,
    demoFilterLabelsByYear: [],
    benchLabelsByYear: [],
    exBenchLabelsByYear: [],
    staticText: {},
};
